import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, HandHeart, Radio, Check, CornerDownRight, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { pushSupported, enablePush, disablePush } from "@/lib/push";
import { toast } from "sonner";

const ICONS = { prayer_prayed: HandHeart, host_live: Radio, event_reply: CornerDownRight, event_comment: MessageCircle, event_reminder: Bell };

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const [pushOn, setPushOn] = useState(() => pushSupported() && typeof Notification !== "undefined" && Notification.permission === "granted");
  const [pushBusy, setPushBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const shown = filter === "all" ? items : items.filter((n) => n.kind === filter);

  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        toast.success("Live push alerts turned off");
      } else {
        const ok = await enablePush();
        if (ok) { setPushOn(true); toast.success("🔔 Live push alerts on — you'll know the moment we go live!"); }
        else toast.error("Enable notifications in your browser to get live alerts");
      }
    } catch (e) { toast.error("Couldn't update alerts"); }
    setPushBusy(false);
  };

  const load = () => api.get("/notifications").then((r) => { setItems(r.data.items); setUnread(r.data.unread); }).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      try { await api.post("/notifications/read-all"); } catch (e) {}
    }
  };

  const openItem = (n) => {
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell" className="relative p-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-300 transition-colors">
        {unread > 0 ? <BellRing className="w-5 h-5 text-[#E6B34A]" /> : <Bell className="w-5 h-5" />}
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-2xl border border-white/10 bg-[#141C42] shadow-2xl z-50" data-testid="notification-panel">
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2 sticky top-0 bg-[#141C42]">
            <Bell className="w-4 h-4 text-[#E6B34A]" />
            <span className="font-display font-bold text-sm tracking-tight">Notifications</span>
          </div>
          {pushSupported() && (
            <button onClick={togglePush} disabled={pushBusy} data-testid="push-toggle" className="w-full flex items-center justify-between gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors">
              <span className="flex items-center gap-2 text-left">
                <BellRing className={`w-4 h-4 ${pushOn ? "text-[#E6B34A]" : "text-zinc-500"}`} />
                <span className="text-xs">
                  <span className="block font-semibold text-white">Live push alerts</span>
                  <span className="block text-[11px] text-zinc-500">Get notified even when the app is closed</span>
                </span>
              </span>
              <span className={`shrink-0 w-10 h-6 rounded-full p-0.5 transition-colors ${pushOn ? "bg-[#E6B34A]" : "bg-white/10"}`}>
                <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${pushOn ? "translate-x-4" : ""}`} />
              </span>
            </button>
          )}
          <div className="flex items-center gap-1 px-3 py-2 border-b border-white/5">
            {[["all", "All"], ["event_reply", "Replies"]].map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`notif-filter-${k}`} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${filter === k ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`}>{label}</button>
            ))}
          </div>
          {shown.length === 0 ? (
            <p className="text-sm text-zinc-500 px-4 py-10 text-center" data-testid="notification-empty">{filter === "event_reply" ? "No replies yet." : "You're all caught up."}</p>
          ) : (
            <div className="divide-y divide-white/5">
              {shown.map((n) => {
                const Icon = ICONS[n.kind] || Check;
                return (
                  <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-${n.id}`} className="w-full text-left px-4 py-3 hover:bg-white/[0.03] transition-colors flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#E6B34A]/10 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[#E6B34A]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-white truncate">{n.title}</p>
                      {n.body && <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{n.body}</p>}
                      <p className="text-[11px] text-zinc-600 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
